import { useCallback, useMemo } from 'react'
import { useParams } from 'react-router'
import { useSelector } from 'react-redux'
import assignmentSelectors from '../store/selectors/assignment.selectors'
import { useFetchQuestionsQuery } from '../store/apis/question.api'
import { useFetchRubricsQuery } from '../store/apis/rubric.api'
import { isNewTextAssignment } from '../utils/assignment.helper'
import { useVersionId } from '../utils/parseURL.helper'
import { getCategoryById } from '../store/apis/utils/selectors/question.api.selectors'

/**
 * Enriches a QuestionRubric with data from the full Rubric (categories, weight, etc).
 */
const enrichQuestionRubric = (questionRubric, rubrics) => {
    if (!questionRubric || !rubrics) return questionRubric
    const fullRubric = rubrics.find(r => r.id === questionRubric.rubricId)
    return {
        ...questionRubric,
        rubric: fullRubric,
        categories: fullRubric?.categories || [],
    }
}

/**
 * Enriches a Question's questionRubrics with categories from full Rubric data.
 */
const enrichQuestion = (question, rubrics) => {
    if (!question || !rubrics) return question
    const enrichedRubrics = question.questionRubrics?.map(qr => enrichQuestionRubric(qr, rubrics)) || []
    return {
        ...question,
        questionRubrics: enrichedRubrics,
        categories: enrichedRubrics.flatMap(qr => 
            (qr.categories || []).map(cat => ({
                ...cat,
                questionRubricId: qr.id,
                rubricId: qr.rubricId,
            }))
        ),
    }
}

/**
 * Enriches all questions with categories from rubrics.
 */
const enrichQuestions = (questions, rubrics) => {
    if (!questions || !rubrics) return questions
    return questions.map(q => enrichQuestion(q, rubrics))
}

/**
 * Finds a QuestionRubric by its ID from questions array.
 */
const findQuestionRubricById = (questions, questionRubricId) => {
    if (!questions || !questionRubricId) return null
    for (const question of questions) {
        const qr = question.questionRubrics?.find(r => r.id === questionRubricId)
        if (qr) return qr
    }
    return null
}

/**
 * Finds a Question that contains a specific QuestionRubric.
 */
const findQuestionByQuestionRubricId = (questions, questionRubricId) => {
    if (!questions || !questionRubricId) return null
    return questions.find(q => q.questionRubrics?.some(r => r.id === questionRubricId)) || null
}

/**
 * Gets all categories from all questionRubrics for a question.
 */
export const getQuestionCategories = question => {
    return (question?.questionRubrics || []).flatMap(qr => qr.categories || [])
}

/**
 * Hook to get rubric context with enriched questions and categories.
 * 
 * @param {Object} options
 * @param {number} [options.questionRubricId] - Optional ID; if not provided, uses layerId from URL
 */
const useRubricContext = ({ questionRubricId: questionRubricIdProp, skip = false } = {}) => {
    const language = useSelector(assignmentSelectors.language)
    const { token, layerId } = useParams()
    const versionId = useVersionId()
    const isTextAssignment = isNewTextAssignment(language)

    const { data: rawQuestions, isFetching: isFetchingQuestions } = useFetchQuestionsQuery(
        { token, versionId },
        { skip: skip || !isTextAssignment },
    )

    const { data: rubrics, isFetching: isFetchingRubrics } = useFetchRubricsQuery(
        { token },
        { skip: skip || !isTextAssignment },
    )

    const isFetching = isFetchingQuestions || isFetchingRubrics
    const isDataReady = rawQuestions && rubrics

    // Enrich all questions with categories from rubrics (only when both are ready)
    const questions = useMemo(
        () => isDataReady ? enrichQuestions(rawQuestions, rubrics) : undefined,
        [rawQuestions, rubrics, isDataReady]
    )

    // Determine view state from URL
    const isAllView = layerId === 'all'
    const isDashboardView = layerId === 'dashboard'
    
    // Use prop if provided, otherwise derive from URL
    const questionRubricId = questionRubricIdProp ?? (
        isAllView || isDashboardView ? null : Number(layerId)
    )
    const isSpecificRubric = questionRubricId != null && !isNaN(questionRubricId)
    
    // Find current rubric and question from enriched data
    const questionRubric = isSpecificRubric ? findQuestionRubricById(questions, questionRubricId) : null
    const question = isSpecificRubric ? findQuestionByQuestionRubricId(questions, questionRubricId) : null
    const questionId = question?.id || null

    // All categories from all questions (flattened)
    const allCategories = useMemo(
        () => questions?.flatMap(q => q.categories || []) || [],
        [questions]
    )
    
    // Check if all rubrics across all questions are trained
    const allRubricsAreTrained = useMemo(
        () => questions?.every(q => q.questionRubrics?.every(r => r.lastTrained)) ?? false,
        [questions]
    )
    
    return {
        // From URL or prop
        layerId,
        questionRubricId,
        
        // Derived data (enriched with categories)
        questionRubric,
        questionId,
        question,
        categories: questionRubric?.categories || [],
        allCategories,
        allRubricsAreTrained,
        getCategoryById: useCallback(
            (questionRubricId) => getCategoryById(questions, questionRubricId),
            [questions]
        ),
        
        // View state
        isAllView,
        isDashboardView,
        isSpecificRubric,
        
        // Loading state
        isFetching,
        
        // All questions data (enriched)
        questions,
    }
}

export default useRubricContext

